/** biome-ignore-all lint/suspicious/useAdjacentOverloadSignatures: methods aren't overloads */
import type { GenericFunction } from '#types/generic-function.type';
import type { InferType } from '#types/infer-result.type';

import { raise } from '#errors/raise';
import { isInstanceOf } from '#misc/is-instance-of';
import { isPromise } from '#misc/is-promise.guard';
import { noop } from '#misc/noop';

import { ResultIterator } from './result-iterator';

const IDENTITY = <T>(value: T): T => {
  return value;
};
const TRUE = () => {
  return true;
};
const FALSE = () => {
  return false;
};

/**
 * Extracts the error types from a tuple or array of Result types.
 *
 * For each element in the input tuple/array, this type extracts the error type (the second generic parameter)
 * from the corresponding Result. If an element is not a Result, it resolves to never.
 *
 * @template T - A readonly array or tuple of Result types.
 *
 * @example
 * type R1 = Result<number, string>;
 * type R2 = Result<boolean, Error>;
 * type Errors = ExtractFailureTypes<[R1, R2]>; // [string, Error]
 */
export type ExtractFailureTypes<T extends readonly Result<unknown, unknown>[]> = {
  [index in keyof T]: T[index] extends Result<unknown, infer E> ? E : never;
};

/**
 * Extracts the success types from a tuple or array of Result types.
 *
 * For each element in the input tuple/array, this type extracts the success type (the first generic parameter)
 * from the corresponding Result. If an element is not a Result, it resolves to never.
 *
 * @template T - A readonly array or tuple of Result types.
 *
 * @example
 * type R1 = Result<number, string>;
 * type R2 = Result<boolean, Error>;
 * type Successes = ExtractSuccessTypes<[R1, R2]>; // [number, boolean]
 */
export type ExtractSuccessTypes<T extends readonly Result<unknown, unknown>[]> = {
  [index in keyof T]: T[index] extends Result<infer U, unknown> ? U : never;
};

/**
 * Infers the error type from a Result type.
 *
 * Given a type R that extends Result, this type extracts the error type (the second generic parameter).
 * If R is not a Result, it resolves to never.
 *
 * @template R - The Result type to extract the error type from.
 *
 * @example
 * type MyResult = Result<number, string>;
 * type ErrorType = InferFailureType<MyResult>; // string
 */
export type InferFailureType<R> = R extends Result<unknown, infer E> ? E : never;

/**
 * Infers the success type from a Result type.
 *
 * Given a type R that extends Result, this type extracts the success type (the first generic parameter).
 * If R is not a Result, it resolves to never.
 *
 * @template R - The Result type to extract the success type from.
 *
 * @example
 * type MyResult = Result<number, string>;
 * type SuccessType = InferSuccessType<MyResult>; // number
 */
export type InferSuccessType<R> = R extends Result<infer T, unknown> ? T : never;

/**
 * Infers the Result type for a given function, handling both synchronous and asynchronous functions.
 *
 * - If the function's return type is `never`, the resulting type is `never`.
 * - If the function returns a Promise, the resulting type is a Promise of a Result wrapping the resolved type.
 * - Otherwise, the resulting type is a Result wrapping the function's return type.
 *
 * @template TFunction - The function type to infer the Result type from.
 *
 * @example
 * type SyncFn = () => number;
 * type SyncResult = AsyncToResult<SyncFn>; // Result<number, unknown>
 *
 * type AsyncFn = () => Promise<string>;
 * type AsyncResult = AsyncToResult<AsyncFn>; // Promise<Result<string, unknown>>
 *
 * type NeverFn = () => never;
 * type NeverResult = AsyncToResult<NeverFn>; // never
 */
type AsyncToResult<TFunction extends GenericFunction> =
  ReturnType<TFunction> extends never
    ? Result<ReturnType<TFunction>, unknown>
    : ReturnType<TFunction> extends Promise<infer U>
      ? Promise<Result<U, unknown>>
      : Result<ReturnType<TFunction>, unknown>;

/**
 * Represents a result of an operation that could be successful or failed.
 *
 * @example
 * ```typescript
 * // Creating a successful result
 * const successResult = Result.success('Operation succeeded');
 *
 * // Creating a failed result
 * const failureResult = Result.failure(new Error('Operation failed'));
 *
 * // Checking if the result is successful
 * if (successResult.isSuccess()) {
 *   console.log(successResult.data); // 'Operation succeeded'
 * } else {
 *   console.error(successResult.error); // Error: Operation failed
 * }
 * ```
 */
export class Result<TData, TError> implements Iterable<InferType<TData>> {
  private readonly data: TData | undefined;

  private readonly error: TError | undefined;

  private readonly failure: boolean;

  private readonly success: boolean;

  private constructor(success: boolean, data?: TData | TError) {
    this.success = success;
    this.failure = !success;
    this.error = this.failure ? (data as TError) : undefined;
    this.data = this.success ? (data as TData) : undefined;
  }

  /**
   * Checks if all provided results are successful.
   *
   * @typeParam T - The type of the Result instances in the array.
   * @param results An array of Result instances to be checked.
   * @returns A Result that indicates success if all provided results are successful,
   * otherwise a Result that indicates failure with the first encountered error.
   *
   * @remarks
   * This method is useful for aggregating multiple Result instances and checking if all operations succeeded.
   *
   * @example
   * ```typescript
   * const results = [
   *   Result.success(1),
   *   Result.success(2),
   *   Result.success(3),
   * ];
   * const allSuccess = Result.all(results);
   * console.log(allSuccess.isSuccess()); // true
   * console.log(allSuccess.data); // [1, 2, 3]
   * const failureResults = [
   *   Result.success(1),
   *   Result.failure(new Error('Failed operation')),
   *   Result.success(3),
   * ];
   * const allFailure = Result.all(failureResults);
   * console.log(allFailure.isFailure()); // true
   * console.log(allFailure.data); // Error: Failed operation
   * ```
   */
  static all<const T extends Result<unknown, unknown>[]>(
    results: T
  ): Result<ExtractSuccessTypes<T>, ExtractFailureTypes<T>[number]> {
    const successes: T[] = [];

    for (const result of results) {
      if (result.isSuccess()) {
        successes.push(result.data as T);
      } else {
        return result as Result<ExtractSuccessTypes<T>, ExtractFailureTypes<T>[number]>;
      }
    }

    return success(successes as ExtractSuccessTypes<T>);
  }

  /**
   * Checks if any of the provided results are successful.
   * @param results  An array of Result instances to be checked.
   * @returns A Result that indicates success with the first successful result,
   * or a Result that indicates failure with an array of errors from all failed results.
   *
   * @remarks
   * This method is useful for checking if at least one operation succeeded among multiple Result instances.
   *
   *  @example
   * ```typescript
   *  const results = [
   *    Result.failure(new Error('Failed operation 1')),
   *    Result.failure(new Error('Failed operation 2')),
   *    Result.success(3),
   *  ];
   *  const anySuccess = Result.any(results);
   *
   *  console.log(anySuccess.isSuccess()); // true
   *  console.log(anySuccess.data); // 3
   *
   *  const failureResults = [
   *    Result.failure(new Error('Failed operation 1')),
   *    Result.failure(new Error('Failed operation 2')),
   *    Result.failure(new Error('Failed operation 3')),
   *  ];
   *  const anyFailure = Result.any(failureResults);
   *
   *  console.log(anyFailure.isFailure()); // true
   *  console.log(anyFailure.data); // [Error: Failed operation 1, Error: Failed operation 2, Error: Failed operation 3]
   * ```
   */
  static any<const T extends Result<unknown, unknown>[]>(
    results: T
  ): Result<ExtractSuccessTypes<T>[number], ExtractFailureTypes<T>> {
    const failures = [];

    for (const result of results) {
      if (result.isSuccess()) {
        return result as Result<ExtractSuccessTypes<T>[number], ExtractFailureTypes<T>>;
      }

      failures.push(result.error);
    }

    return failure(failures as ExtractFailureTypes<T>);
  }

  /**
   * Creates a Result representing a failed operation.
   *
   * @typeParam TError - The type of the error details.
   * @param error The error details of the failed operation.
   * @returns A Result that indicates failure with the provided error details.
   */
  static failure<TError>(this: void, error: TError): Result<never, TError> {
    // false positive: TError is inferrable but omitting it requires partial type args (unsupported),
    // which causes TData to be inferred as TError instead of never
    return new Result<never, TError>(false, error);
  }

  /**
   * Creates a Result from a function that may return a value or a Promise.
   *
   * @param fn A function that returns a value or a Promise.
   * @returns {AsyncToResult<TFunction>} A Result that wraps the value or a Promise that resolves to a Result,
   * depending on whether the input function returns a Promise or not.
   *
   * @remarks
   * This method handles both synchronous and asynchronous functions.
   *
   * @example
   * ```typescript
   * const syncFn = () => {
   *   return 'Hello, World!';
   * };
   * const syncResult = Result.from(syncFn);
   * console.log(syncResult.isSuccess()); // true
   * console.log(syncResult.data); // 'Hello, World!'
   * ```
   * @example
   * ```typescript
   * const asyncFn = async () => {
   *   return 'Hello, Async World!';
   * };
   * const asyncResult = Result.from(asyncFn);
   * asyncResult.then(result => {
   *   console.log(result.isSuccess()); // true
   *    console.log(result.data); // 'Hello, Async World!'
   * });
   * ```
   */
  static from<TFunction extends GenericFunction>(fn: TFunction): AsyncToResult<TFunction> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const value = fn();

      if (isPromise(value)) {
        return value.then(Result.success).catch(Result.failure) as unknown as AsyncToResult<TFunction>;
      }

      return Result.success(value) as unknown as AsyncToResult<TFunction>;
    } catch (error) {
      return Result.failure(error) as unknown as AsyncToResult<TFunction>;
    }
  }

  /**
   * Type guard: checks if the given value is a Result and a failure.
   */
  static isFailure<_T, E>(value: unknown): value is Result<never, E> {
    return value instanceof Result && value.isFailure();
  }

  /**
   * Checks if the provided value is an instance of Result.
   *
   * @typeParam TData - The type of the data contained in the Result.
   * @param value The value to check if it is a Result instance.
   * @returns `true` if the value is an instance of Result, otherwise `false`.
   *
   * @example
   * ```typescript
   * const result = Result.success(42);
   * console.log(Result.isResult(result)); // true
   * console.log(Result.isResult({})); // false
   * ```
   */
  static isResult(value: unknown): value is Result<unknown, unknown> {
    return value instanceof Result;
  }

  /**
   * Type guard: checks if the given value is a Result and a success.
   */
  static isSuccess<T, _E>(value: unknown): value is Result<T, never> {
    return value instanceof Result && value.isSuccess();
  }

  /**
   * Partitions an array of Result instances into two arrays:
   * one containing the data from successful Result instances
   * one containing the data from failed Result instances
   *
   * @typeParam T - The type of the Result instances in the array.
   * @param results  An array of Result instances to be partitioned.
   * @returns A tuple containing two arrays:
   *          - The first array contains the data from successful Result instances.
   *          - The second array contains the data from failed Result instances.
   *
   * @example
   * ```typescript
   *  const results = [
   *    Result.success(1),
   *    Result.failure(new Error('Failed operation')),
   *    Result.success(2),
   *    Result.failure(new Error('Another failure')),
   *  ];
   *  const [successes, failures] = Result.partition(results);
   *
   * console.log(successes); // [1, 2]
   *  console.log(failures); // [Error: Failed operation, Error: Another failure]
   * ```
   */
  static partition<T extends Result<unknown, unknown>[]>(results: T): [ExtractSuccessTypes<T>, ExtractFailureTypes<T>] {
    return results.reduce(
      ([successes, errors], result) => {
        return result.isSuccess()
          ? [[...successes, result.data] as ExtractSuccessTypes<T>, errors]
          : [successes, [...errors, result.error] as ExtractFailureTypes<T>];
      },
      [[] as ExtractSuccessTypes<T>, [] as ExtractFailureTypes<T>]
    );
  }

  /**
   * Creates a Result representing a successful operation.
   *
   * @typeParam TData - The type of the data returned by the successful operation.
   * @param data The data returned by the successful operation.
   * @returns A Result that indicates success with the provided data.
   */
  static success<TData = unknown>(this: void, data?: TData): Result<TData, never> {
    return new Result<TData, never>(true, data);
  }

  /**
   * Wraps a function to return a Result.
   *
   * @typeParam T - The type of the function to wrap.
   * @param fn The function to wrap.
   * @returns A function that returns a Result when called.
   *
   * @remarks
   * This method is useful for converting synchronous functions into Result-wrapped functions.
   *
   * @example
   * ```typescript
   * const wrappedFn = Result.wrap((x: number) => x + 1);
   * const result = wrappedFn(5);
   * console.log(result.isSuccess()); // true
   * console.log(result.data); // 6
   * ```
   */
  static wrap<T extends GenericFunction>(fn: T): (...args: Parameters<T>) => AsyncToResult<T> {
    return (...args: Parameters<T>): AsyncToResult<T> => {
      return Result.from(() => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return fn(...args);
      }) as AsyncToResult<T>;
    };
  }

  /**
   *
   * @param result A Result to combine with the current Result.
   * @returns If the current Result is a success, it returns the provided Result.
   *
   * @example
   * ```typescript
   * const result1 = Result.success(42);
   * const result2 = Result.success('Hello');
   * const combinedResult = result1.and(result2);
   * console.log(combinedResult.isSuccess()); // true
   * console.log(combinedResult.data); // 'Hello'
   * ```
   * @example
   * ```typescript
   * * const failureResult = Result.failure(new Error('Something went wrong'));
   * const combinedFailure = result1.and(failureResult);
   * console.log(combinedFailure.isFailure()); // true
   * console.log(combinedFailure.data); // Error: Something went wrong
   * ```
   */
  and<U>(result: Result<U, TError>): Result<U, TError> {
    return this.match(
      () => {
        return result;
      },
      (value) => {
        return Result.failure(value);
      }
    );
  }

  /**
   * Combines the current Result with another Result, returning a new Result.
   * If the current Result is a success, it returns the provided Result.
   * If the current Result is a failure, it returns a failure Result with the original error.
   *
   * @param mapper A function that maps the contained data to a new value.
   * @returns A Result containing the mapped value if the Result is a success,
   * otherwise a Result containing the original error.
   *
   * @example
   * ```typescript
   * const result = Result.success(42);
   * const mappedResult = result.andThen((value) => Result.success(value + 1));
   * console.log(mappedResult.isSuccess()); // true
   * console.log(mappedResult.data); // 43
   * ```
   * @example
   * ```typescript
   * const failureResult = Result.failure(new Error('Something went wrong'));
   * const mappedFailure = failureResult.andThen((value) => Result.success(value + 1));
   * console.log(mappedFailure.isFailure()); // true
   * console.log(mappedFailure.data); // Error: Something went wrong
   * ```
   */
  andThen<U, E = TError>(mapper: (value: TData) => Result<U, E | TError>): Result<U, E | TError> {
    return this.match(mapper, (error) => {
      return Result.failure(error as E | TError);
    });
  }

  /**
   * Inspects the Result and executes a callback if it is a success.
   *
   * @param onSuccess A callback function that is executed if the Result is a success.
   * @return The Result instance for chaining.
   *
   * @example
   * ```typescript
   * const result = Result.success(42);
   * result.inspect((data) => {
   *   console.log(`Success with data: ${data}`);
   * }); // Logs: "Success with data: 42"
   * ```
   * @example
   * ```typescript
   * const failureResult = Result.failure(new Error('Something went wrong'));
   * failureResult.inspect((data) => {
   *  console.log(`This will not be called because the result is a failure`);
   * }); // No output
   * ```
   */
  inspect(onSuccess: (data: TData) => void): this {
    this.match(onSuccess, noop);

    return this;
  }

  /**
   * Inspects the Result and executes a callback if it is a failure.
   * @param onFailure A callback function that is executed if the Result is a failure.
   * @return The Result instance for chaining.
   *
   * @example
   * ```typescript
   * const result = Result.failure(new Error('Something went wrong'));
   * result.inspectFailure((error) => {
   *   console.error(`Failure with error: ${error.message}`);
   * }); // Logs: "Failure with error: Something went wrong"
   * ```
   * @example
   * ```typescript
   * const successResult = Result.success(42);
   *   successResult.inspectFailure((error) => {
   *   console.error(`This will not be called because the result is a success`);
   * }); // No output
   * ```
   */
  inspectFailure(onFailure: (error: TError) => void): this {
    this.match(noop, onFailure);

    return this;
  }

  /**
   * Checks if the Result is a failure.
   * @returns `true` if the Result is a failure, otherwise `false`.
   *
   * @example
   * ```typescript
   * const result = Result.failure(new Error('Something went wrong'));
   * console.log(result.isFailure()); // true
   * ```
   */
  isFailure(): this is Result<never, TError> {
    return this.match(FALSE, TRUE);
  }

  /**
   * Checks if the Result is a failure and the contained error satisfies the provided predicate.
   * @param predicate A function that checks if the error matches a condition.
   * @returns `true` if the Result is a failure and the error matches the predicate, otherwise `false`.
   *
   * @example
   * ```typescript
   * const result = Result.failure(new Error('Something went wrong'));
   * const isFailureAndSpecific = result.isFailureAnd((error) => error.message.includes('went wrong'));
   * console.log(isFailureAndSpecific); // true
   * ```
   */
  isFailureAnd(predicate: (error: TError) => boolean): this is Result<never, TError> {
    return this.match(FALSE, predicate);
  }

  /**
   * Checks if the Result is a success.
   * @returns `true` if the Result is a success, otherwise `false`.
   *
   * @example
   * ```typescript
   * const result = Result.success(42);
   * console.log(result.isSuccess()); // true
   * ```
   */
  isSuccess(): this is Result<TData, never> {
    return this.match(TRUE, FALSE);
  }

  /**
   * Checks if the Result is a success and the contained data satisfies the provided predicate.
   * @param predicate A function that checks if the data matches a condition.
   * @returns `true` if the Result is a success and the data matches the predicate, otherwise `false`.
   *
   * @example
   * ```typescript
   * const result = Result.success(42);
   * const isSuccessAndEven = result.isSuccessAnd((value) => value % 2 === 0);
   * console.log(isSuccessAndEven); // true
   * ```
   */
  isSuccessAnd(predicate: (data: TData) => boolean): this is Result<TData, never> {
    return this.match(predicate, FALSE);
  }

  /**
   * Transforms the contained data to a new value if the Result is a success,
   * otherwise returns a Result with the original error.
   * @param mapper A function that maps the contained data to a new value.
   * @returns A Result containing the mapped value if the Result is a success,
   * otherwise a Result containing the original error.
   *
   * @example
   * ```typescript
   * const result = Result.success(42);
   * const mappedResult = result.map((value) => value + 1);
   * console.log(mappedResult.isSuccess()); // true
   * console.log(mappedResult.data); // 43
   * ```
   * @example
   * ```typescript
   * const failureResult = Result.failure(new Error('Something went wrong'));
   * const mappedFailure = failureResult.map((value) => value + 1);
   * console.log(mappedFailure.isFailure()); // true
   * console.log(mappedFailure.data); // Error: Something went wrong
   * ```
   */
  map<U>(mapper: (data: TData) => U): Result<U, TError> {
    return this.match(
      (data) => {
        return Result.success(mapper(data));
      },
      (error) => {
        return Result.failure(error) as Result<U, TError>;
      }
    );
  }

  /**
   * Transforms the contained data to a new value or returns a default value if the Result is a failure.
   * @param defaultValue The default value to return if the Result is a failure.
   * @param mapper A function that maps the contained data to a new value.
   * @returns The mapped value if the Result is a success, otherwise the default value.
   *
   * @example
   * ```typescript
   * const result = Result.success(42);
   * const mappedValue = result.mapOr(0, (value) => value + 1);
   * console.log(mappedValue); // 43
   *
   * const failureResult = Result.failure(new Error('Something went wrong'));
   * const defaultValue = failureResult.mapOr(0, (value) => value + 1);
   * console.log(defaultValue); // 0
   * ```
   */
  mapOr<U>(defaultValue: U, mapper: (data: TData) => U): U {
    return this.match(mapper, () => {
      return defaultValue;
    });
  }

  /**
   * Transforms the contained data to a new value or returns a value from a default mapper function
   * if the Result is a failure.
   * @param defaultMapper A function that returns a default value if the Result is a failure.
   * @param mapper A function that maps the contained data to a new value.
   * @returns The mapped value if the Result is a success, otherwise the value returned by the default mapper function.
   *
   * @example
   * ```typescript
   * const result = Result.success(42);
   * const mappedValue = result.mapOrElse(() => 'default', (value) => `Value: ${value}`);
   * console.log(mappedValue); // "Value: 42"
   * const failureResult = Result.failure(new Error('Something went wrong'));
   * const defaultValue = failureResult.mapOrElse(() => 'default', (value) => `Value: ${value}`);
   * console.log(defaultValue); // "default"
   * ```
   */
  mapOrElse<U>(defaultMapper: () => U, mapper: (data: TData) => U): U {
    return this.match(mapper, () => {
      return defaultMapper();
    });
  }

  /**
   * Allows iteration over the Result. If the Result is a success, yields the contained data.
   * If the Result is a failure, yields nothing.
   *
   * @example
   * ```typescript
   * const result = Result.success([42,43]);
   * for (const value of result) {
   *   console.log(value); // 42, 43
   * }
   *
   * const failureResult = Result.failure(new Error('fail'));
   * for (const value of failureResult) {
   *   // This block will not execute
   * }
   * ```
   */
  [Symbol.iterator](): Iterator<InferType<TData>> {
    if (this.isFailure()) {
      return {
        next: () => {
          return { done: true, value: undefined };
        },
        throw: (e?: unknown) => {
          throw e;
        },
      };
    }

    const data = this.unwrap();

    if (Array.isArray(data)) {
      return new ResultIterator<InferType<TData>>([...data]);
    }

    return new ResultIterator<InferType<TData>>(data as [InferType<TData>]);
  }

  /**
   * Returns a string representation of the Result.
   * @returns A string indicating whether the Result is a success or failure, along with the contained data or error.
   *
   * @example
   * ```typescript
   * const result = Result.success(42);
   * console.log(result.toString()); // "Success(42)"
   *
   * const failureResult = Result.failure(new Error('Something went wrong'));
   * console.log(failureResult.toString()); // "Failure(Error: Something went wrong)"
   * ```
   */
  toString() {
    return this.match(
      (data) => {
        return `Success(${JSON.stringify(data)})`;
      },
      (error) => {
        if (isInstanceOf(error, Error)) {
          return `Failure(${error.toString()})`;
        }

        return `Failure(${JSON.stringify(error)})`;
      }
    );
  }

  /**
   * Unwraps the data from a successful Result or throws an error if the Result is a failure.
   * @returns {TData} The data contained in the Result if it is a success.
   * @throws {BaseError} If the Result is a failure, it throws an error with a message
   * indicating that unwrapping failed.
   *
   * @example
   * ```typescript
   * const result = Result.success(42);
   * const data = result.unwrap();
   * console.log(data); // 42
   * ```
   */
  unwrap(): TData {
    return this.expect('called `Result.unwrap()` on a `Failure` value');
  }

  /**
   * Unwraps the error from a failed Result or throws an error if the Result is a success.
   * @returns {TError} The error contained in the Result if it is a failure.
   * @throws {BaseError} If the Result is a success, it throws an error with a message
   * indicating that unwrapping failed.
   *
   * @example
   * ```typescript
   * const failureResult = Result.failure(new Error('Something went wrong'));
   * const error = failureResult.unwrapErr();
   * console.log(error.message); // 'Something went wrong'
   * ```
   */
  unwrapFailure(): TError {
    return this.expectFailure('called `Result.unwrapFailure()` on a `Success` value');
  }

  /**
   * Unwraps the data from a successful Result or returns a default value if the Result is a failure.
   *
   * @param defaultValue The default value to return if the Result is a failure.
   * @returns {TData} The data contained in the Result if it is a success, otherwise the default value.
   *
   * @example
   * ```typescript
   * const result = Result.success(42);
   * const data = result.unwrapOr(0);
   * console.log(data); // 42
   * ```
   * @example
   * ```typescript
   * const failureResult = Result.failure(new Error('Something went wrong'));
   * const defaultValue = failureResult.unwrapOr(0);
   * console.log(defaultValue); // 0
   * ```
   */
  unwrapOr(defaultValue: TData): TData {
    return this.match(IDENTITY, () => {
      return defaultValue;
    });
  }

  private expect(message: string): TData {
    return this.match(IDENTITY, (error) => {
      return raise(message, { cause: error });
    });
  }

  private expectFailure(message: string): TError {
    return this.match((value) => {
      return raise(message, { cause: value });
    }, IDENTITY);
  }

  private match<U>(onSuccess: (data: TData) => U, onFailure: (error: TError) => U): U {
    if (this.success) {
      return onSuccess(this.data as TData);
    }

    return onFailure(this.error as TError);
  }
}

export const failure: typeof Result.failure = Result.failure;

export const success: typeof Result.success = Result.success;
